<template>
  <div class="d-flex align-items-center">
    <div class="prio-filter d-flex" style="align-items: center; gap: 2px">
      <button
        v-for="priority in priorities"
        :key="priority.text"
        :id="priority.text"
        @click="onPriorityClick"
        class="prio-btn"
        :class="{
          'prio-btn--active':
            prioritiesSelected.includes(priority.text) ||
            (prioritiesSelected.length === 0 && priority.text === 'Alle'),
        }"
      >
        {{ priority.text }}
      </button>
    </div>

    <button
      class="zusatztermin-btn"
      :class="{ 'zusatztermin-btn--active': showZusatztermine }"
      @click="onZusatztermineClick"
    >
      Z
    </button>

    <!-- Land -->
    <AppSelect
      v-model="filterValues.country"
      apply-mode="button"
      :options="countryOptions"
      label="Land"
      multiple
      search-placeholder="Land suchen..."
      style="margin-left: 6px; min-width: 100px; max-width: 100px"
      @input="emitChange"
    />

    <!-- Reisekürzel -->
    <AppSelect
      v-model="filterValues.reisekuerzel"
      apply-mode="button"
      :options="reisekuerzelOptions"
      label="Reisekürzel"
      multiple
      search-placeholder="Kürzel suchen..."
      style="margin-left: 6px; min-width: 145px; max-width: 145px"
      @input="emitChange"
    />

    <!-- Status -->
    <AppSelect
      v-model="filterValues.state"
      apply-mode="button"
      :options="stateOptions"
      label="Status"
      multiple
      search-placeholder="Status suchen..."
      style="margin-left: 6px; min-width: 105px; max-width: 105px"
      @input="emitChange"
    />

    <!-- Startdatum Range -->
    <AppDatePicker
      ref="startdatumPicker"
      v-model="filterValues.startdatumRange"
      range
      append-to-body
      format="DD.MM.YYYY"
      range-separator="-"
      placeholder="Startdatum"
      style="margin-left: 6px; max-width: 185px; min-width: 185px"
      @input="emitChange"
      @calendar-change="onCalChange"
    >
      <template #footer>
        <AppDatePickerShortcuts
          :jahr="calendarSelectedYear"
          @select="onBereich('startdatumRange', 'startdatumPicker', $event)"
        />
      </template>
    </AppDatePicker>

    <!-- Releasedatum Range -->
    <AppDatePicker
      ref="releasedatumPicker"
      v-model="filterValues.releasedatum"
      range
      append-to-body
      format="DD.MM.YYYY"
      range-separator="-"
      placeholder="Releasedatum"
      :disabled-date="dateDisabledReleasedatum"
      style="margin-left: 6px; max-width: 195px; min-width: 195px"
      @input="emitChange"
      @calendar-change="onCalChange"
    >
      <template #footer>
        <AppDatePickerShortcuts
          :jahr="calendarSelectedYear"
          @select="onBereich('releasedatum', 'releasedatumPicker', $event)"
        />
      </template>
    </AppDatePicker>

    <!-- Country Manager -->
    <AppSelect
      v-model="filterValues.countryManager"
      apply-mode="button"
      :options="countryManagerOptions"
      label="CM"
      multiple
      search-placeholder="CM suchen..."
      style="margin-left: 6px; min-width: 95px; max-width: 95px"
      @input="emitChange"
    />

    <b-form-input
      v-model="filterValues.forecastUmsatzAtRisk"
      :formatter="formatForecastUmsatzAtRisk"
      class="toolbar-filter fc-umsatz-filter"
      size="sm"
      placeholder="FC Umsatz at risk"
      title="Zum Filtern kann eine Zahl, eine Range oder </> für die Suche eingegeben werden."
      v-b-tooltip.hover="{ customClass: 'fc-umsatz-tooltip' }"
      @change="emitChange"
      style="margin-left: 6px; max-width: 135px; min-width: 135px"
    />

    <div
      class="icon-button d-flex justify-content-center text-danger"
      :style="{
        marginLeft: '6px',
        width: '36px',
        minWidth: '36px',
        cursor: isFilterActive ? 'pointer' : 'default',
        visibility: isFilterActive ? 'visible' : 'hidden',
      }"
      @click="onFilterReset"
      v-b-tooltip.hover.bottom
      title="Filter zurücksetzen"
    >
      <span class="d-flex align-items-center"><FunnelX :size="20" /></span>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { FunnelX } from 'lucide-vue';
import * as odataService from '@/core/common/services/odata.service';
import AppSelect from '@/components/common/app-select/app-select.vue';
import AppDatePicker from '@/components/common/app-date-picker/app-date-picker.vue';
import AppDatePickerShortcuts from '@/components/common/app-date-picker/app-date-picker-shortcuts.vue';
import { reiseCountryName } from '@/core/common/iso3166Countries.js';

const BLANK_ANGELEGT_STATES = [
  'AvailableInBlank',
  'AvailableInBlankAndSyncedHotelleistungWithRelations',
  'AvailableInBlankAndSyncedHotelleistungWithRelationsAndKontingente',
  'AvailableInBlankAndSyncedHotelleistungWithRelationsAndKontingenteAndZusatzleistungen',
];

const STATE_LABELS = Object.freeze({
  CmBestaetigt: 'CM bestätigt',
  CmAngefragt: 'CM angefragt',
  Durchgefuehrt: 'Durchgeführt',
  Durchfuehrung: 'Durchführung',
});

export default {
  name: 'TouristischePlanungToolbarFilter',
  components: {
    FunnelX,
    AppSelect,
    AppDatePicker,
    AppDatePickerShortcuts,
  },

  data() {
    return {
      priorities: [
        { value: null, text: 'Alle' },
        { value: 0, text: 'A' },
        { value: 1, text: 'B' },
        { value: 2, text: 'C' },
        { value: 3, text: 'D' },
      ],
      prioritiesSelected: [],
      showZusatztermine: false,
      filterValues: this.getDefaultFilterValues(),
      allFilterRows: [],
      calendarSelectedYear: new Date().getFullYear(),
      filterOptionsRequestId: 0,
    };
  },

  computed: {
    // Prüft, ob der Startdatum-Range gegenüber dem Default (01.01.-31.12. des aktuellen Jahres) verändert wurde
    isStartdatumRangeChanged() {
      const range = this.filterValues.startdatumRange;
      if (!range || !range[0] || !range[1]) return true;
      const year = new Date().getFullYear();
      return (
        range[0].getFullYear() !== year ||
        range[0].getMonth() !== 0 ||
        range[0].getDate() !== 1 ||
        range[1].getFullYear() !== year ||
        range[1].getMonth() !== 11 ||
        range[1].getDate() !== 31
      );
    },

    isFilterActive() {
      const v = this.filterValues;
      return !!(
        v.country.length > 0 ||
        v.reisekuerzel.length > 0 ||
        v.state.length > 0 ||
        this.isStartdatumRangeChanged ||
        (v.releasedatum?.length > 0 && v.releasedatum[0]) ||
        v.countryManager.length > 0 ||
        v.forecastUmsatzAtRisk ||
        this.prioritiesSelected.length > 0 ||
        this.showZusatztermine
      );
    },

    // Die folgenden computeds bauen die Optionen für die MultiSelect-Dropdowns auf
    countryOptions() {
      const rows = this.getFilteredRows('country');
      const values = [...new Set(rows.map(row => row.reise?.country).filter(Boolean))].sort();
      return values.map(v => ({ value: v, text: reiseCountryName(v) }));
    },

    reisekuerzelOptions() {
      const rows = this.getFilteredRows('reisekuerzel');
      const values = [...new Set(rows.map(row => row.reise?.reisekuerzel).filter(Boolean))].sort();
      return values;
    },

    stateOptions() {
      const rows = this.getFilteredRows('state');
      const values = [...new Set(rows.map(row => row.state).filter(Boolean))].sort();
      return values.map(v => ({ value: v, text: this.formatState(v) }));
    },

    countryManagerOptions() {
      const rows = this.getFilteredRows('countryManager');
      const cms = rows.flatMap(row => (row.countryManagers || []).map(cm => cm.countryManagerKuerzel));
      return [...new Set(cms)].filter(Boolean).sort();
    },

    availableReleasedaten() {
      const rows = this.getFilteredRows('releasedatum');
      return new Set(rows.map(row => row.releasedatum?.substring(0, 10)).filter(Boolean));
    },
  },

  watch: {
    // Die Optionen hängen am geladenen Zeitraum, deshalb wird bei jeder neuen Range nachgeladen
    'filterValues.startdatumRange': {
      handler() {
        this.loadFilterOptions();
      },
    },
  },

  created() {
    // Einmalig den initialen Filter-State an den Parent emitten, damit dieser
    // die Defaults nicht selbst kennen muss
    this.emitChange();
  },

  async mounted() {
    await this.loadFilterOptions();
  },

  methods: {
    formatState(state) {
      return STATE_LABELS[state] || state;
    },

    /**
     * `excludeField` bestimmt, WELCHER Filter NICHT angewendet werden soll,
     * damit man im Dropdown noch alle anderen Optionen sehen kann, die in
     * Kombination mit den übrigen Filtern gültig wären.
     *
     * Jedes Dropdown zeigt alle Optionen, die zu den AKTUELL gesetzten
     * anderen Filtern passen. Der eigene Filter wird dabei ausgenommen
     * (excludeField), damit der User seine Auswahl für dieses Dropdown
     * jederzeit ändern oder erweitern kann
     */
    getFilteredRows(excludeField) {
      return this.allFilterRows.filter(row => {
        const { country, reisekuerzel, state, countryManager, startdatumRange, releasedatum } =
          this.filterValues;

        // Land
        if (excludeField !== 'country' && country.length > 0 && !country.includes(row.reise?.country))
          return false;

        // Reisekürzel
        if (
          excludeField !== 'reisekuerzel' &&
          reisekuerzel.length > 0 &&
          !reisekuerzel.includes(row.reise?.reisekuerzel)
        )
          return false;

        // Status
        if (excludeField !== 'state' && state.length > 0 && !state.includes(row.state)) return false;

        // Country Manager – Row hat einen Array von CMs. `some` prüft,
        // ob mindestens ein CM der Row in der Filterauswahl steht
        if (excludeField !== 'countryManager' && countryManager.length > 0) {
          if (!row.countryManagers?.some(cm => countryManager.includes(cm.countryManagerKuerzel)))
            return false;
        }

        // Startdatum. endOfDay stellt sicher, dass auch Termine am
        // "Ende"-Tag inklusive sind (sonst würden 00:00-Termine noch rein,
        // aber z. B. 14:00 rausfallen)
        if (
          excludeField !== 'startdatumRange' &&
          startdatumRange &&
          startdatumRange[0] &&
          startdatumRange[1]
        ) {
          const rowDate = row.startdatum ? new Date(row.startdatum) : null;
          if (rowDate) {
            const endOfDay = new Date(startdatumRange[1]);
            endOfDay.setHours(23, 59, 59);
            if (rowDate < startdatumRange[0] || rowDate > endOfDay) return false;
          }
        }

        // Releasedatum. Hier wird ein Halb-Intervall [start, end+1Tag) verwendet,
        // das sauberer mit Zeitzonen umgeht als "<= end 23:59:59"
        if (excludeField !== 'releasedatum' && releasedatum?.length === 2 && releasedatum[0]) {
          const rowRelease = row.releasedatum ? new Date(row.releasedatum) : null;
          if (!rowRelease) return false;
          const releaseStart = new Date(releasedatum[0]);
          const releaseEnd = new Date(releasedatum[1]);
          releaseEnd.setDate(releaseEnd.getDate() + 1);
          if (rowRelease < releaseStart || rowRelease >= releaseEnd) return false;
        }

        if (this.showZusatztermine && !row.istZusatztermin) return false;

        // FC Umsatz at risk. Unterstützt Zahl, Range und Vergleichsoperatoren
        if (
          excludeField !== 'forecastUmsatzAtRisk' &&
          !this.matchesNumericFilter(row.forecastUmsatzAtRisk, this.filterValues.forecastUmsatzAtRisk)
        )
          return false;

        if (this.prioritiesSelected.length > 0) {
          const selectedValues = this.prioritiesSelected.map(
            text => this.priorities.find(p => p.text === text)?.value
          );
          if (!selectedValues.includes(row.reise?.prioritaet)) return false;
        }

        return true;
      });
    },

    async loadFilterOptions() {
      const requestId = ++this.filterOptionsRequestId;
      try {
        const range = this.filterValues.startdatumRange;
        const filter = [
          { reiseterminSyncState: { in: BLANK_ANGELEGT_STATES } },
          { state: { ne: 'Verworfen' } },
          { isDeleted: { ne: true } },
        ];
        // Der geladene Zeitraum folgt der Startdatum-Auswahl, damit die Optionen
        // auch außerhalb des aktuellen Jahres gefüllt sind
        if (range?.[0]) {
          filter.push({ startdatum: { ge: new Date(range[0]) } });
        }
        if (range?.[1]) {
          const endDate = new Date(range[1]);
          endDate.setHours(23, 59, 59);
          filter.push({ startdatum: { le: endDate } });
        }

        const response = await odataService.getReisetermin({
          filter,
          select: ['id', 'state', 'startdatum', 'releasedatum', 'istZusatztermin', 'forecastUmsatzAtRisk'],
          expand: {
            reise: { select: ['reisekuerzel', 'country', 'prioritaet'] },
            countryManagers: { select: ['countryManagerKuerzel'] },
          },
        });
        if (requestId === this.filterOptionsRequestId) {
          this.allFilterRows = response.data || [];
        }
      } catch (e) {
        console.error('Fehler beim Laden der Filteroptionen:', e);
      }
    },

    // Bootstrap-Vue Formatter: erlaubt nur Ziffern, Leerzeichen und die Vergleichszeichen < > = -
    formatForecastUmsatzAtRisk(value) {
      return String(value ?? '').replace(/[^0-9<>=\s-]/g, '');
    },

    dateDisabledReleasedatum(date) {
      const ymd = format(date, 'yyyy-MM-dd');
      return !this.availableReleasedaten.has(ymd);
    },

    // Speichert das gerade im Kalender angezeigte Jahr, damit Monats-Shortcuts auf dieses Jahr wirken
    onCalChange(date) {
      this.calendarSelectedYear = date.getFullYear();
    },

    // Übernimmt die Range aus der Schnellauswahl und schließt das Popup
    onBereich(feld, refName, [von, bis]) {
      this.calendarSelectedYear = von.getFullYear();
      this.filterValues[feld] = [von, bis];
      this.$refs[refName]?.closePopup();
      this.emitChange();
    },

    onPriorityClick($event) {
      const { id: priorityText } = $event.target;

      if (priorityText === 'Alle') {
        this.prioritiesSelected = [];
      } else if (!this.prioritiesSelected.includes(priorityText)) {
        this.prioritiesSelected.push(priorityText);
      } else {
        this.prioritiesSelected = this.prioritiesSelected.filter(p => p !== priorityText);
      }
      // Wenn alle einzelnen Prioritäten aktiv sind, resettet die Auswahl wieder auf "Alle"
      if (this.prioritiesSelected.length === this.priorities.length - 1) {
        this.prioritiesSelected = [];
      }

      this.emitChange();
    },

    onZusatztermineClick() {
      this.showZusatztermine = !this.showZusatztermine;
      this.emitChange();
    },

    onFilterReset() {
      this.filterValues = this.getDefaultFilterValues();
      this.prioritiesSelected = [];
      this.showZusatztermine = false;
      this.emitChange();
    },

    // Setzt die Text-Auswahl der Prio-Buttons auf die zugehörigen Enum-Werte um
    getPrioritiesValues() {
      return this.prioritiesSelected.map(text => this.priorities.find(p => p.text === text)?.value);
    },

    emitChange() {
      this.$emit('change', {
        ...this.filterValues,
        forecastUmsatzAtRiskFilter: this.parseNumericComparisonFilter(this.filterValues.forecastUmsatzAtRisk),
        prioritiesValues: this.getPrioritiesValues(),
        showZusatztermine: this.showZusatztermine,
      });
    },

    // Wandelt die Texteingabe ("123", "100-500", "1000 - 2000", ">500", ">=500", "<200", "<=200") in einen OData-Vergleich um
    parseNumericComparisonFilter(value) {
      if (value === null || value === undefined) return null;
      const str = String(value).trim();
      if (!str) return null;

      const rangeMatch = str.match(/^(\d+)\s*-\s*(\d+)$/);
      if (rangeMatch) {
        return { ge: parseInt(rangeMatch[1]), le: parseInt(rangeMatch[2]) };
      }

      if (str.startsWith('>=')) {
        const n = parseInt(str.slice(2));
        return isNaN(n) ? null : { ge: n };
      }
      if (str.startsWith('<=')) {
        const n = parseInt(str.slice(2));
        return isNaN(n) ? null : { le: n };
      }
      if (str.startsWith('>')) {
        const n = parseInt(str.slice(1));
        return isNaN(n) ? null : { gt: n };
      }
      if (str.startsWith('<')) {
        const n = parseInt(str.slice(1));
        return isNaN(n) ? null : { lt: n };
      }

      const n = parseInt(str);
      return isNaN(n) ? null : { eq: n };
    },

    // Prüft eine Zahl gegen den geparsten Filter (ge/le/gt/lt/eq). Kein Filter → immer true
    matchesNumericFilter(rowValue, filterString) {
      const filter = this.parseNumericComparisonFilter(filterString);
      if (!filter) return true;
      if (rowValue == null) return false;
      if (filter.eq != null && rowValue !== filter.eq) return false;
      if (filter.ge != null && rowValue < filter.ge) return false;
      if (filter.le != null && rowValue > filter.le) return false;
      if (filter.gt != null && rowValue <= filter.gt) return false;
      if (filter.lt != null && rowValue >= filter.lt) return false;
      return true;
    },

    getDefaultFilterValues() {
      const year = new Date().getFullYear();
      return {
        country: [],
        reisekuerzel: [],
        state: [],
        startdatumRange: [new Date(year, 0, 1), new Date(year, 11, 31)],
        releasedatum: [],
        countryManager: [],
        forecastUmsatzAtRisk: null,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.prio-btn {
  border: 1px solid var(--border);
  background: var(--surface);
  color: #4b4b4b;
  font-weight: 500;
  font-size: 13px;
  padding: 4px 0;
  border-radius: 0;
  height: 38px;
  width: 27px;
  text-align: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:first-child {
    width: 43px;
    border-radius: 5px 0 0 5px;
  }

  &:last-child {
    border-radius: 0 5px 5px 0;
  }

  &:hover {
    background: #f0f0f0;
  }

  &--active {
    background: #bedbff;
    border-color: #bedbff;

    &:hover {
      background: #aed0ff;
      border-color: #aed0ff;
    }
  }
}

.toolbar-filter.custom-select,
.toolbar-filter.form-control {
  height: 38px;
  line-height: 24px;
  color: #4b4b4b;
  border: 1px solid var(--border);
  box-shadow: none;

  &:focus {
    border-color: #69b3ff;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075), 0 0 0 0.2rem rgba(54, 153, 255, 0.25);
  }
}

:deep(.app-select) {
  .show > .dropdown-toggle,
  .dropdown-toggle:focus {
    border-color: #69b3ff !important;
  }
}

:deep(.toolbar-filter.b-form-datepicker) {
  height: 38px;
  border: 1px solid var(--border);
  box-shadow: none;

  .form-control {
    height: 38px;
    line-height: 24px;
    color: #4b4b4b;
    border: none;
    box-shadow: none;
  }

  label.form-control {
    display: flex;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
    line-height: 38px;
    font-family: 'Poppins', sans-serif;
    font-size: 0.875rem !important;
  }
}

.datepicker-placeholder :deep(label.form-control) {
  color: #4b4b4b !important;
}

.zusatztermin-btn {
  border: 1px solid var(--border);
  background: var(--surface);
  color: #4b4b4b;
  font-weight: 600;
  font-size: 13px;
  padding: 4px 0;
  border-radius: 5px;
  height: 38px;
  width: 25px;
  min-width: 25px;
  text-align: center;
  cursor: pointer;
  margin-left: 6px;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #f0f0f0;
  }

  &--active {
    background: #bedbff;
    border-color: #bedbff;

    &:hover {
      background: #aed0ff;
      border-color: #aed0ff;
    }
  }
}
</style>

<style lang="scss">
.fc-umsatz-filter::placeholder {
  color: var(--text-body);
}

.fc-umsatz-tooltip {
  opacity: 1 !important;

  .tooltip-inner {
    background-color: var(--surface) !important;
    color: #4b4b4b;
    text-align: left;
    opacity: 1 !important;
  }
}
</style>
